import { Injectable } from '@angular/core';
import { HttpInterceptorService } from '../../core/services/http-interceptor.service';
import { IRevision, IRevisionCheckpoint, IWorkflow } from '../../core/interfaces/workflow.interface';
import { ReviseType } from '../constants/revise-types.constant';
import { Observable } from 'rxjs/Observable';
import { get } from 'lodash';
import { DynamicModalLoaderService } from '@ispm/layout/services/dynamic-modal-loader.service';
import { ModalTypes } from '@ispm/layout/components/modals/contants/modal-types.enum';
import { ModalLoaderPayload } from '@ispm/layout/classes/modal-loader-payload.abstract';
import { flatMap, map } from 'rxjs/operators';
import { modalResponse } from '@ispm/layout/constants/modal-responses.enum';
import { IDefinition } from '../../configuration/interfaces/definition.interface';
import { NOTIFICATION_MESSAGE } from '@ispm-client/modules/aggregator/services/consts';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';

@Injectable()
export class ReviseService {

  static readonly BASE_URL = '/workflow/reviseAnalysis';

  constructor(private _http: HttpInterceptorService,
              private notificationMessageService: NotificationMessageService,
              private notificationMessage: NOTIFICATION_MESSAGE,
              private popupLoader: DynamicModalLoaderService) {
  }

  public static filterAvailableCheckpoints(revisions: Array<IRevision>, addUIElements: boolean = true): Array<IRevisionCheckpoint> {
    if (!revisions) {
      return [];
    }
    revisions = [...revisions].reverse();
    if (!(Array.isArray(revisions[0].checkpoints) && revisions[0].checkpoints.length > 0)) {
      return [];
    }
    const rev = revisions[0];
    return rev.checkpoints.filter(checkpoint => {
      let requiredStepFound = false;
      // return !!revisions.find((rev) => {//multiple revision query - assume you touch the step 2 revisions back <- very problematic
      if (rev.items) {
        requiredStepFound = !!rev.items.reverse().find((item) => {
          if (item.purpose.toLowerCase() === checkpoint.requiredStepPurpose.toLowerCase()
            && item.status.toLowerCase() !== 'pending') {
            return true;
          }

        });
      }

      return requiredStepFound && !!rev.items.reverse().find((item) => {
        if (item.purpose.toLowerCase() === checkpoint.revertToStepPurpose.toLowerCase()) {
          if (addUIElements) {
            checkpoint['ui'] = item['ui'];
            this.setCheckpointDisplayName(checkpoint);
          }
          return true;
        }
      });
      // })
    });
  }

  public static isReviseDefinitionAvailable(definition: IDefinition, workflow: IWorkflow, revision: IRevision): boolean {
    let requiredStep, forceReviseItem, reviseAllowed;
    const allowNonePending = get(definition.revise, 'allowNonePending', false);

    const requiredStepForRevise = definition.revise && definition.revise.requiredStep && definition.revise.requiredStep.toLowerCase() || 'reviewtestresults';
    const requiredStepForReviseStatus = get(definition.revise, 'requiredStepStatus', 'done').toLowerCase();

    const allowReviseStep = definition.revise && definition.revise.allowReviseStep && definition.revise.allowReviseStep.toLowerCase() || 'reviewtestresults';
    const allowReviseStepStatus = get(definition.revise, 'allowReviseStepStatus', 'done').toLowerCase();

    if (revision.items === undefined) {
      revision.items = [];
    }
    forceReviseItem = allowReviseStep && revision.items.find(revise => revise.name && revise.name.toLowerCase() === allowReviseStep);
    forceReviseItem = forceReviseItem && (forceReviseItem.status.toLowerCase() === allowReviseStepStatus
      || (allowNonePending && forceReviseItem.status.toLowerCase() !== 'pending'));

    reviseAllowed = (workflow.status.toLowerCase() === 'done' || forceReviseItem);

    if (reviseAllowed) {
      return true;
    } else if (workflow.activeRevision.toLowerCase() === 'none') {
      return true;
    } else if (revision.id === workflow.activeRevision
      && !!(requiredStep = revision.items.find(item => item.name && item.name.toLowerCase() === requiredStepForRevise))
      && requiredStep
      && (requiredStep.status.toLowerCase() === requiredStepForReviseStatus
        || (allowNonePending && requiredStep.status.toLowerCase() !== 'pending'))) {
      return true;
    } else {
      return false;
    }
  }

  public static getAvailableTemplates(definition: IDefinition, workflow: IWorkflow, revision: IRevision): Array<Partial<IRevisionCheckpoint>> {
    if (this.isReviseDefinitionAvailable(definition, workflow, revision) && definition.workflowJobsTemplates) {
      return definition.workflowJobsTemplates.reduce((arr, item) => {
        if (item.usage === 'revision') {
          arr.push({
            display: item.name,
            revertToStepPurpose: item.reference,
            type: ReviseType.TEMPLATE
          });
        }
        return arr;
      }, []);
    }
    return [];
  }

  public static setCheckpointDisplayName(checkpoint: IRevisionCheckpoint): IRevisionCheckpoint {
    const uiConf = get(checkpoint, 'ui.workflowVisibility.barItems');
    if (uiConf) {
      const uiItem = uiConf.find(item => item && item.isActive);
      checkpoint.display = checkpoint.display || uiItem.name;
    }
    return checkpoint;
  }

  public backToPreviousStep(backOption: BackOption) {
    const type = backOption.revisionCheckpoint.type || ReviseType.MANUAL;
    return this.revise(type, backOption.workflowId, backOption.revisionId, backOption.revisionCheckpoint.revertToStepPurpose, true);
  }

  public openReviseModal(options: OpenReviseModalOptions) {

    const revisionCheckpoint = options.revisionCheckpoint;
    const workflowId = options.workflowId;
    const revisionId = options.revisionId;

    return this.popupLoader.popupModal(options.component,
      options.payload,
      ModalTypes.ALERT
    ).pipe(
      map((res) => res.payload),
      flatMap((res: modalResponse) => {
        switch (res) {
          case modalResponse.approve:
            const type = revisionCheckpoint.type || ReviseType.MANUAL;
            return this.revise(type, workflowId, revisionId, revisionCheckpoint.revertToStepPurpose);
          default:
            return Observable.empty();
        }
      })
    );
  }

  public revise(type: ReviseType, workflowId: string, revisionId: string, subjectId: string, isBackJob?: boolean) {
    return this._http.post(ReviseService.BASE_URL, {
      type,
      workflowId,
      revisionId,
      subjectId,
      isBackJob
    }, true)
      .map(res => {
        if (isBackJob) {
          this.notificationMessageService.success(this.notificationMessage.SUCCESSFULLY_BACK_TO_PREVIOUS_STEP);

        } else {
          this.notificationMessageService.success(this.notificationMessage.ITEM_REVISED_SUCCESSFULLY);
        }
        return res.json();
      })
      .catch(err => {
        if (isBackJob) {
          this.notificationMessageService.error(this.notificationMessage.FAILED_BACK_TO_PREVIOUS_STEP);

        } else {
          this.notificationMessageService.error(this.notificationMessage.ITEM_REVISE_FAILED);
        }
        this.notificationMessageService.error(this.notificationMessage.ITEM_REVISE_FAILED);
        return Observable.empty();
      });
  }
}

export class OpenReviseModalOptions {
  revisionCheckpoint: IRevisionCheckpoint;
  component: any;
  payload: ModalLoaderPayload;
  workflowId: string;
  revisionId: string;

  constructor(options: { [key in keyof OpenReviseModalOptions]: any }) {
    this.revisionCheckpoint = options.revisionCheckpoint;
    this.component = options.component;
    this.payload = options.payload;
    this.workflowId = options.workflowId;
    this.revisionId = options.revisionId;
  }

}

export class BackOption {
  revisionCheckpoint: IRevisionCheckpoint;
  workflowId: string;
  revisionId: string;

  constructor(options: { [key in keyof OpenReviseModalOptions]: any }) {
    this.revisionCheckpoint = options.revisionCheckpoint;
    this.workflowId = options.workflowId;
    this.revisionId = options.revisionId;
  }

}
